import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PageShell, SlideCorner } from "@/components/Layout";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft } from "lucide-react";

/* ============================================================
   /build/brand — Commissioning brief for brands, labels,
   publishers, agencies and cultural institutions.
   ============================================================ */
export default function BuildBrand() {
  const [form, setForm] = useState({
    company: "", contact_name: "", contact_email: "",
    project_type: "", budget_range: "", timeline: "", brief: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.contact_email || !form.brief) { toast.error("Email and brief are required"); return; }
    setSubmitting(true);
    try {
      await api.post("/briefs", { ...form, intent: "brand" });
      setDone(true);
      toast.success("Brief received. We'll be in touch.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally { setSubmitting(false); }
  };

  return (
    <PageShell>
      <section className="relative grid-bg border-b border-border">
        <SlideCorner right="BUILD · FOR BRANDS" />
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 pt-16 pb-16">
          <Link to="/build" className="meta-mono !text-white/55 hover:!text-white transition inline-flex items-center gap-2 mb-10" data-testid="build-brand-back">
            <ArrowLeft size={12} /> BACK TO BUILD
          </Link>
          <p className="meta-mono !text-primary mb-6">— FOR BRANDS, LABELS &amp; INSTITUTIONS</p>
          <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-bold leading-[0.92] tracking-tight">
            Tell us the <span className="hl-cyan">project.</span><br />
            We come back with a <span className="hl-coral">plan.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-white/80 text-base md:text-lg leading-relaxed">
            For brands, labels, publishers, agencies and cultural institutions looking to commission or produce work with Indian creatives. We reply with an approach and a costed proposal — no obligation to proceed. Every brief is read by a human, not routed through a form-processor.
          </p>
        </div>
      </section>

      <section className="border-b border-border">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 py-24 md:py-32">
          {done ? (
            <div className="border border-secondary/40 bg-secondary/[0.04] p-10" data-testid="build-brand-success">
              <p className="label-mono !text-secondary mb-4">✓ RECEIVED</p>
              <h2 className="font-display text-4xl md:text-5xl font-bold leading-tight">Thanks. We&apos;ll be in touch <span className="hl-cyan">shortly.</span></h2>
              <p className="mt-6 text-white/75 max-w-2xl">Someone from the team reads every brief personally. Expect a reply within 3 working days at <strong>{form.contact_email}</strong>.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="grid md:grid-cols-2 gap-px bg-border" data-testid="build-brand-form">
              <Field label="You / your company / collective" v={form.company} onChange={(v) => setForm({ ...form, company: v })} testid="brand-company" />
              <Field label="Contact name" v={form.contact_name} onChange={(v) => setForm({ ...form, contact_name: v })} testid="brand-name" />
              <Field label="Email" type="email" v={form.contact_email} onChange={(v) => setForm({ ...form, contact_email: v })} testid="brand-email" />
              <Field label="What is it? (campaign / film / record / event / product…)" v={form.project_type} onChange={(v) => setForm({ ...form, project_type: v })} testid="brand-type" />
              <Field label="Budget (or 'still figuring out')" v={form.budget_range} onChange={(v) => setForm({ ...form, budget_range: v })} testid="brand-budget" />
              <Field label="Timeline" v={form.timeline} onChange={(v) => setForm({ ...form, timeline: v })} testid="brand-timeline" />
              <div className="md:col-span-2 bg-background p-5">
                <p className="meta-mono mb-2">— TELL US ABOUT THE PROJECT</p>
                <textarea data-testid="brand-brief" value={form.brief} onChange={(e) => setForm({ ...form, brief: e.target.value })} rows={6} placeholder="What are you making? Who's it for? What's the ambition? What's already in place?" className="w-full bg-transparent border border-border p-3 outline-none focus:border-primary text-sm" />
              </div>
              <button data-testid="brand-submit" disabled={submitting} className="btn-pill btn-coral md:col-span-2 justify-center py-4">
                {submitting ? "Sending…" : <>Submit brief <ArrowRight size={14} /></>}
              </button>
            </form>
          )}
        </div>
      </section>
    </PageShell>
  );
}

function Field({ label, v, onChange, testid, type = "text" }) {
  return (
    <div className="bg-background p-5">
      <p className="meta-mono mb-2">— {label.toUpperCase()}</p>
      <input data-testid={testid} type={type} value={v} onChange={(e) => onChange(e.target.value)} className="w-full bg-transparent outline-none border-b border-border focus:border-primary py-2 text-base font-light" />
    </div>
  );
}
